import { IconArrowLeft, IconArrowRight, IconChartPie } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
} from '@/components/ui/table';

interface GrossBenefitData {
  totalBenefit: number;
  virtualCardRebate: number;
  achPlusRebate: number;
  checkSavings: number;
  virtualCardPercentage: number;
  achPlusDiscount: number;
  achPlusKeepPercentage: number;
  averageCheckCost: number;
}
interface PaymentScorecardData {
  steerfi: number;
  birchstreetDemo: number;
  current: number;
  other: number;
}
interface SupplierTypeRow {
  type: string;
  inProgram: boolean;
  transactionCount: number;
  supplierCount: number;
  totalSpend: number;
  estimatedVirtualCard: number;
  achPlus: number;
  achBasic: number;
  remainOnCheck: number;
}

interface OurApproachComponentProps {
  companyName: string;
  onNext?: () => void;
  onPrevious?: () => void;
  onReset?: () => void;
}

export const OurApproachComponent = ({
  companyName,
  onNext,
  onPrevious,
  onReset,
}: OurApproachComponentProps) => {
  const [effectiveRebate, setEffectiveRebate] = useState(0);

  const grossBenefitData: GrossBenefitData = {
    totalBenefit: 449329.65,
    virtualCardRebate: 280820.43,
    achPlusRebate: 140321.22,
    checkSavings: 28184.0,
    virtualCardPercentage: 1.6,
    achPlusDiscount: 2,
    achPlusKeepPercentage: 25,
    averageCheckCost: 7,
  };
  const paymentScorecardData: PaymentScorecardData = {
    steerfi: 85.0,
    birchstreetDemo: 15.0,
    current: 1,
    other: 40,
  };
  const supplierTypesData: SupplierTypeRow[] = [
    {
      type: 'General',
      inProgram: true,
      transactionCount: 15888,
      supplierCount: 582,
      totalSpend: 42026291.0,
      estimatedVirtualCard: 6303343.65,
      achPlus: 1786117.37,
      achBasic: 27148983.99,
      remainOnCheck: 6787246.0,
    },
    {
      type: 'Strategic',
      inProgram: true,
      transactionCount: 3392,
      supplierCount: 42,
      totalSpend: 37896356.0,
      estimatedVirtualCard: 3789635.6,
      achPlus: 1705336.02,
      achBasic: 25921107.5,
      remainOnCheck: 6480276.88,
    },
    {
      type: 'Network Name Match',
      inProgram: true,
      transactionCount: 3840,
      supplierCount: 105,
      totalSpend: 16353591.0,
      estimatedVirtualCard: 8176795.5,
      achPlus: 408839.78,
      achBasic: 6214364.58,
      remainOnCheck: 1553591.14,
    },
    {
      type: 'Exempt',
      inProgram: false,
      transactionCount: 600,
      supplierCount: 49,
      totalSpend: 5594445.0,
      estimatedVirtualCard: 0.0,
      achPlus: 0.0,
      achBasic: 0.0,
      remainOnCheck: 5594445.0,
    },
    {
      type: 'International',
      inProgram: false,
      transactionCount: 40,
      supplierCount: 8,
      totalSpend: 263001.0,
      estimatedVirtualCard: 0.0,
      achPlus: 0.0,
      achBasic: 0.0,
      remainOnCheck: 263001.0,
    },
    {
      type: 'Individual',
      inProgram: false,
      transactionCount: 230,
      supplierCount: 45,
      totalSpend: 970568.0,
      estimatedVirtualCard: 0.0,
      achPlus: 0.0,
      achBasic: 0.0,
      remainOnCheck: 970568.0,
    },
    {
      type: 'Low Volume',
      inProgram: true,
      transactionCount: 390,
      supplierCount: 181,
      totalSpend: 1051170.0,
      estimatedVirtualCard: 0.0,
      achPlus: 0.0,
      achBasic: 0.0,
      remainOnCheck: 1051170.0,
    },
    {
      type: 'Network Name Match Virtual Card',
      inProgram: true,
      transactionCount: 812,
      supplierCount: 12,
      totalSpend: 1218848.0,
      estimatedVirtualCard: 976297.25,
      achPlus: 12127.54,
      achBasic: 184338.57,
      remainOnCheck: 46084.64,
    },
    {
      type: 'Network Name Match Ach Basic',
      inProgram: true,
      transactionCount: 228,
      supplierCount: 4,
      totalSpend: 344465.0,
      estimatedVirtualCard: 24112.55,
      achPlus: 16017.62,
      achBasic: 243467.86,
      remainOnCheck: 60866.97,
    },
  ];

  const totalRow = supplierTypesData.reduce(
    (acc, row) => ({
      transactionCount: acc.transactionCount + row.transactionCount,
      supplierCount: acc.supplierCount + row.supplierCount,
      totalSpend: acc.totalSpend + row.totalSpend,
      estimatedVirtualCard: acc.estimatedVirtualCard + row.estimatedVirtualCard,
      achPlus: acc.achPlus + row.achPlus,
      achBasic: acc.achBasic + row.achBasic,
      remainOnCheck: acc.remainOnCheck + row.remainOnCheck,
    }),
    {
      transactionCount: 0,
      supplierCount: 0,
      totalSpend: 0,
      estimatedVirtualCard: 0,
      achPlus: 0,
      achBasic: 0,
      remainOnCheck: 0,
    }
  );

  const formatCurrency = (amount: number): string =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  const formatNumber = (num: number): string => new Intl.NumberFormat('en-US').format(num);

  useEffect(() => {
    const targetRebate = 0.3;
    const duration = 500;
    const steps = 60;
    const increment = targetRebate / steps;
    const stepDuration = duration / steps;
    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const currentValue = Math.min(increment * currentStep, targetRebate);
      setEffectiveRebate(Math.round(currentValue * 100) / 100);
      if (currentStep >= steps) {
        clearInterval(timer);
      }
    }, stepDuration);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <IconChartPie className="h-6 w-6" />
        <div>
          <h1 className="text-xl font-semibold">Our Approach</h1>
          <p className="text-sm text-muted-foreground">
            Birchstreet's optimized payment solution for {companyName}
          </p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader>
            <CardTitle as="h2" className="text-lg">
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 bg-green-500 rounded-full" />
                Optimized Gross Benefit
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-8">
                {formatCurrency(grossBenefitData.totalBenefit)}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-stretch">
                <div className="bg-green-100 p-3 h-full">
                  <div className="text-lg font-bold text-green-800">
                    {formatCurrency(grossBenefitData.virtualCardRebate)}
                  </div>
                  <div className="text-xs text-green-800 font-medium">Virtual Card Rebate</div>
                  <div className="text-xs text-green-800">
                    {grossBenefitData.virtualCardPercentage}%
                  </div>
                </div>
                <div className="bg-blue-100 p-3 h-full">
                  <div className="text-lg font-bold text-blue-700">
                    {formatCurrency(grossBenefitData.achPlusRebate)}
                  </div>
                  <div className="text-xs text-blue-700 font-medium">ACH Plus Rebate</div>
                  <div className="text-xs text-blue-800">
                    {grossBenefitData.achPlusDiscount}% | {grossBenefitData.achPlusKeepPercentage}%
                  </div>
                </div>
                <div className="bg-teal-100 p-3 h-full">
                  <div className="text-lg font-bold text-teal-700">
                    {formatCurrency(grossBenefitData.checkSavings)}
                  </div>
                  <div className="text-xs text-teal-800 font-medium">Check Savings</div>
                  <div className="text-xs text-teal-800">${grossBenefitData.averageCheckCost}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle as="h2" className="text-lg text-center">
              Effective Rebate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center mt-4 h-full">
              <div className="text-7xl font-bold text-blue-600 mb-2">
                {effectiveRebate.toFixed(2)}%
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle as="h2" className="text-lg text-center">
              SteerFi Effort
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center space-y-4">
              <div className="relative mx-auto w-32 h-32">
                <svg
                  className="w-full h-full transform -rotate-90"
                  viewBox="0 0 120 120"
                  aria-label="SteerFi effort distribution chart"
                >
                  <title>SteerFi effort distribution chart</title>
                  <circle cx="60" cy="60" r="54" fill="none" stroke="#e5e7eb" strokeWidth="12" />
                  <circle
                    cx="60"
                    cy="60"
                    r="54"
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="12"
                    strokeDasharray={`${(paymentScorecardData.steerfi / 100) * 339.292}, 339.292`}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-bold text-foreground">
                    {paymentScorecardData.steerfi}%
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span>SteerFi</span>
                <span className="font-semibold">{paymentScorecardData.steerfi}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle as="h2" className="text-lg text-center">
              Payment Scorecard
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center space-y-4">
              <div className="relative mx-auto w-32 h-32">
                <svg
                  className="w-full h-full"
                  viewBox="0 0 120 120"
                  aria-label="Payment scorecard distribution"
                >
                  <title>Payment scorecard distribution</title>
                  <circle cx="60" cy="60" r="50" fill="none" stroke="#e5e7eb" strokeWidth="20" />
                  <circle
                    cx="60"
                    cy="60"
                    r="50"
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="20"
                    strokeDasharray="166.50 314.16"
                    strokeDashoffset="0"
                    transform="rotate(-90 60 60)"
                  />
                  <circle
                    cx="60"
                    cy="60"
                    r="50"
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="20"
                    strokeDasharray="3.14 314.16"
                    strokeDashoffset="-166.50"
                    transform="rotate(-90 60 60)"
                  />
                  <circle
                    cx="60"
                    cy="60"
                    r="50"
                    fill="none"
                    stroke="#f97316"
                    strokeWidth="20"
                    strokeDasharray="125.66 314.16"
                    strokeDashoffset="-169.64"
                    transform="rotate(-90 60 60)"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg font-bold">53%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle as="h2" className="text-lg">
            Top 15 Card Match Suppliers
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            {/* Not the card title: it says "Suppliers", but the rows are
                supplier categories. A caption is a screen-reader user's only
                description of the rows, so it can't inherit that error. */}
            <Table caption="Card match summary by supplier type">
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-center">In Program</TableHead>
                  <TableHead className="text-right">Transaction Count</TableHead>
                  <TableHead className="text-right">Supplier Count</TableHead>
                  <TableHead className="text-right">Total Spend</TableHead>
                  <TableHead className="text-right">Estimated Virtual Card</TableHead>
                  <TableHead className="text-right">ACH Plus</TableHead>
                  <TableHead className="text-right">ACH Basic</TableHead>
                  <TableHead className="text-right">Remain On Check</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {supplierTypesData.map((row) => (
                  <TableRow key={row.type}>
                    <TableRowHeader className="font-medium">{row.type}</TableRowHeader>
                    <TableCell className="text-center">
                      {row.inProgram ? (
                        <span className="text-green-700 dark:text-green-400 font-bold">✓</span>
                      ) : (
                        <span className="text-red-600 dark:text-red-400 font-bold">✗</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatNumber(row.transactionCount)}
                    </TableCell>
                    <TableCell className="text-right">{formatNumber(row.supplierCount)}</TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(row.totalSpend)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(row.estimatedVirtualCard)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(row.achPlus)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(row.achBasic)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(row.remainOnCheck)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2 font-semibold bg-muted/50">
                  <TableCell>Total</TableCell>
                  <TableCell />
                  <TableCell className="text-right">
                    {formatNumber(totalRow.transactionCount)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatNumber(totalRow.supplierCount)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCurrency(totalRow.totalSpend)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCurrency(totalRow.estimatedVirtualCard)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCurrency(totalRow.achPlus)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCurrency(totalRow.achBasic)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCurrency(totalRow.remainOnCheck)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {(onPrevious || onNext) && (
        <Card>
          <CardContent>
            <div className="flex justify-between items-center">
              <div>
                {onPrevious && (
                  <Button variant="outline" onClick={onPrevious} className="gap-2">
                    <IconArrowLeft className="h-4 w-4" />
                    Previous: Other People's Approach
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <h2 className="font-semibold leading-tight">Ready for next step?</h2>
                  <p className="text-sm text-muted-foreground">
                    Proceed to finalize proposal and next steps
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {onReset && (
                    <Button variant="outline" onClick={onReset}>
                      Reset Workflow
                    </Button>
                  )}
                  {onNext && (
                    <Button onClick={onNext} className="gap-2">
                      Complete Analysis
                      <IconArrowRight className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
